import {
  CAMPAIGN_TYPE_COUPON,
  CAMPAIGN_TYPE_STAMP,
  CAMPAIGN_TYPE_TICKET,
  EARNING_RULE_BIRTH,
  EARNING_RULE_GPS,
  EARNING_RULE_LEVEL_UPGRADE,
  EARNING_RULE_NEW_MEMBER,
  EARNING_RULE_PURCHASE,
  EARNING_RULE_QR_CODE,
  EARNING_RULE_REFER,
} from 'services/CampaignAPIHelper';
import { ssoAPIHelper } from 'services/SSOAPIHelper';

import { createAction, enLocaleNumberFormatter } from 'utils';
import { subHeaderLink } from 'utils/CampaignUtil';
import {
  EarningRuleGlobalLimitInfoType,
  EarningRuleGlobalLimitPeriod,
  EarningRuleGlobalLimitTarget,
} from 'utils/CustomEnums';
import {
  getActivityLogDate,
  getActivityLogDateTime,
} from 'utils/DateTimeUtils';
import i18n from 'utils/I18n';
import { getCMSLanguage } from 'utils/LanguageCheckUtil';
import {
  enLocaleDollarFormatter,
  enLocaleIntegerFormatter,
} from 'utils/NumberUtils';
import ServerConfig from 'utils/ServerConfig';

function groupBy(objectArray, property) {
  return objectArray.reduce((array, obj) => {
    const key = obj[property];
    const newArray = array;
    if (!newArray[key]) {
      newArray[key] = [];
    }
    newArray[key].push(obj);
    return newArray;
  }, {});
}

export const ACTIVITY_LOG_TYPES = {
  CUSTOMER_REWARD_EARNED: 'CUSTOMER_REWARD_EARNED',

  CUSTOMER_POINTS_USED: 'CUSTOMER_POINTS_USED',
  CUSTOMER_POINTS_EXPIRED: 'CUSTOMER_POINTS_EXPIRED',
  CUSTOMER_POINTS_ADMIN_ADDED: 'CUSTOMER_POINTS_ADMIN_ADDED',
  CUSTOMER_POINTS_ADMIN_REMOVED: 'CUSTOMER_POINTS_ADMIN_REMOVED',
  CUSTOMER_POINTS_ADMIN_AUTO_ADDED: 'CUSTOMER_POINTS_ADMIN_AUTO_ADDED',
  CUSTOMER_POINTS_ADMIN_AUTO_REMOVED: 'CUSTOMER_POINTS_ADMIN_AUTO_REMOVED',
  CUSTOMER_POINTS_MERCHANT_ADDED: 'CUSTOMER_POINTS_MERCHANT_ADDED',
  CUSTOMER_POINTS_MERCHANT_REMOVED: 'CUSTOMER_POINTS_MERCHANT_REMOVED',

  CUSTOMER_REWARD_RECALL: 'CUSTOMER_REWARD_RECALL',
  CUSTOMER_LEVEL_CHANGED: 'CUSTOMER_LEVEL_CHANGED',
  CUSTOMER_LEVEL_RETAINED: 'CUSTOMER_LEVEL_RETAINED',

  CUSTOMER_COUPON_EXPIRED: 'CUSTOMER_COUPON_EXPIRED',
  CUSTOMER_COUPON_USED: 'CUSTOMER_COUPON_USED',
  ADMIN_COUPON_USED: 'ADMIN_COUPON_USED',
  MERCHANT_COUPON_USED: 'MERCHANT_COUPON_USED',
  CUSTOMER_COUPON_ADMIN_RECLAIM: 'CUSTOMER_COUPON_ADMIN_RECLAIM',
  CUSTOMER_COUPON_ADMIN_GRANT: 'CUSTOMER_COUPON_ADMIN_GRANT',
  CUSTOMER_COUPON_MERCHANT_RECLAIM: 'CUSTOMER_COUPON_MERCHANT_RECLAIM',
  CUSTOMER_COUPON_MERCHANT_GRANT: 'CUSTOMER_COUPON_MERCHANT_GRANT',

  CUSTOMER_TICKET_EXPIRED: 'CUSTOMER_TICKET_EXPIRED',
  CUSTOMER_TICKET_USED: 'CUSTOMER_TICKET_USED',
  ADMIN_TICKET_USED: 'ADMIN_TICKET_USED',
  MERCHANT_TICKET_USED: 'MERCHANT_TICKET_USED',
  CUSTOMER_TICKET_ADMIN_RECLAIM: 'CUSTOMER_TICKET_ADMIN_RECLAIM',
  CUSTOMER_TICKET_ADMIN_GRANT: 'CUSTOMER_TICKET_ADMIN_GRANT',
  CUSTOMER_TICKET_MERCHANT_RECLAIM: 'CUSTOMER_TICKET_MERCHANT_RECLAIM',
  CUSTOMER_TICKET_MERCHANT_GRANT: 'CUSTOMER_TICKET_MERCHANT_GRANT',

  CUSTOMER_STAMP_EARNED: 'CUSTOMER_STAMPS_EARNED',
  CUSTOMER_STAMP_EXPIRED: 'CUSTOMER_STAMPS_EXPIRED',
  CUSTOMER_STAMPS_ADMIN_RECLAIM: 'CUSTOMER_STAMPS_ADMIN_RECLAIM',
  CUSTOMER_STAMPS_ADMIN_ADD: 'CUSTOMER_STAMPS_ADMIN_ADD',
  CUSTOMER_STAMPS_ADMIN_REMOVE: 'CUSTOMER_STAMPS_ADMIN_REMOVE',
  CUSTOMER_STAMPS_MERCHANT_ADD: 'CUSTOMER_STAMPS_MERCHANT_ADD',
  CUSTOMER_STAMPS_MERCHANT_REMOVE: 'CUSTOMER_STAMPS_MERCHANT_REMOVE',

  CUSTOMER_LOGIN: 'CUSTOMER_LOGIN',
  CUSTOMER_LOGOUT: 'CUSTOMER_LOGOUT',

  CUSTOMER_CASH_DOLLAR_ON_SHOPIFY: 'CUSTOMER_CASH_DOLLAR',
  CUSTOMER_CASH_DOLLAR_BY_MERCHANT_SCANNING:
    'CUSTOMER_CASH_DOLLAR_BY_MERCHANT_SCANNING',
  MERCHANT_DIRECT_USE_CASH_DOLLAR: 'MERCHANT_DIRECT_USE_CASH_DOLLAR',

  CUSTOMER_USE_GIFT_CARD: 'CUSTOMER_USE_GIFT_CARD',

  PURCHASE_REFUND: 'PURCHASE_REFUND',
  PURCHASE_PARTIAL_REFUND: 'PURCHASE_PARTIAL_REFUND',
  CASH_DOLLAR_RETURNED: 'CASH_DOLLAR_RETURNED',
  COUPON_RETURNED: 'COUPON_RETURNED',

  COUPON_TRANSFERRED: 'COUPON_TRANSFERRED',
  TICKET_TRANSFERRED: 'TICKET_TRANSFERRED',
  STAMP_TRANSFERRED: 'STAMP_TRANSFERRED',
  POINT_TRANSFERRED: 'POINT_TRANSFERRED',
  CUSTOMER_TRANSFERRED_BY_ADMIN: 'CUSTOMER_TRANSFERRED_BY_ADMIN',
};

const EARNING_TYPES_MAP = {
  [EARNING_RULE_GPS]: {
    title: 'gpsCheckIn',
    icon: '/activityLog/GPS.png',
    srcSet: '/activityLog/GPS@2x.png 2x, /activityLog/GPS@3x.png 3x',
  },
  [EARNING_RULE_QR_CODE]: {
    title: 'offer_scan_qr_code',
    icon: '/activityLog/scan.png',
    srcSet: '/activityLog/scan@2x.png 2x, /activityLog/scan@3x.png 3x',
  },
  [EARNING_RULE_REFER]: {
    title: 'referral',
    icon: '/activityLog/referral.png',
    srcSet: '/activityLog/referral@2x.png 2x, /activityLog/referral@3x.png 3x',
  },
  [EARNING_RULE_PURCHASE]: {
    title: 'generalPurchase',
    icon: '/activityLog/purchase.png',
    srcSet: '/activityLog/purchase@2x.png 2x, /activityLog/purchase@3x.png 3x',
  },
  [EARNING_RULE_NEW_MEMBER]: {
    title: 'newMember',
    icon: '/activityLog/newMember.png',
    srcSet:
      '/activityLog/newMember@2x.png 2x, /activityLog/newMember@3x.png 3x',
  },
  [EARNING_RULE_BIRTH]: {
    title: 'birthday',
    icon: '/activityLog/birthday.png',
    srcSet: '/activityLog/birthday@2x.png 2x, /activityLog/birthday@3x.png 3x',
  },
  [EARNING_RULE_LEVEL_UPGRADE]: {
    title: 'levelUpgrade',
    icon: '/activityLog/level-upgrade.png',
    srcSet:
      '/activityLog/level-upgrade@2x.png 2x, /activityLog/level-upgrade@3x.png 3x',
  },
};

const ADMIN_EDIT_NAMES_MAP = {
  [ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_ADMIN_ADDED]: 'adminPointsAdded',
  [ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_ADMIN_REMOVED]: 'adminPointsRemoved',
  [ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_ADMIN_AUTO_ADDED]: 'adminPointsAutoAdded',
  [ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_ADMIN_AUTO_REMOVED]:
    'adminPointsAutoRemoved',
  [ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_MERCHANT_ADDED]:
    'adminPointsAddedMerchant',
  [ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_MERCHANT_REMOVED]:
    'adminPointsRemovedMerchant',
  [ACTIVITY_LOG_TYPES.CUSTOMER_REWARD_RECALL]: 'adminPointsRecall',
  [ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_ADMIN_GRANT]: 'adminCouponGrant',
  [ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_ADMIN_GRANT]: 'adminTicketGrant',
  [ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_ADMIN_RECLAIM]: 'adminCouponReclaim',
  [ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_ADMIN_RECLAIM]: 'adminTicketReclaim',
  [ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_MERCHANT_GRANT]:
    'adminCouponGrantMerchant',
  [ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_MERCHANT_GRANT]:
    'adminTicketGrantMerchant',
  [ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_MERCHANT_RECLAIM]:
    'adminCouponReclaimMerchant',
  [ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_MERCHANT_RECLAIM]:
    'adminTicketReclaimMerchant',
  [ACTIVITY_LOG_TYPES.ADMIN_COUPON_USED]: 'adminCouponUsed',
  [ACTIVITY_LOG_TYPES.ADMIN_TICKET_USED]: 'adminTicketUsed',
  [ACTIVITY_LOG_TYPES.MERCHANT_COUPON_USED]: 'adminCouponUsedMerchant',
  [ACTIVITY_LOG_TYPES.MERCHANT_TICKET_USED]: 'adminTicketUsedMerchant',
};

const COUPON_LOGS_NAME_MAP = {
  [ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_USED]: 'couponUsed',
  [ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_USED]: 'ticketUsed',
  [ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_EXPIRED]: 'couponExpired',
  [ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_EXPIRED]: 'ticketExpired',
  [ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_USED]: 'couponRedemption',
  [ACTIVITY_LOG_TYPES.COUPON_RETURNED]: 'activityLogCouponReturned',
  [ACTIVITY_LOG_TYPES.COUPON_TRANSFERRED]: 'couponTransferred',
  [ACTIVITY_LOG_TYPES.TICKET_TRANSFERRED]: 'ticketTransferred',
};

const STAMP_LOGS_NAME_MAP = {
  [ACTIVITY_LOG_TYPES.CUSTOMER_STAMP_EARNED]: 'stampEarned',
  [ACTIVITY_LOG_TYPES.CUSTOMER_STAMP_EXPIRED]: 'stampExpired',
  [ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_ADMIN_RECLAIM]: 'stampUpdatedByAdmin',
  [ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_ADMIN_REMOVE]: 'stampUpdatedByAdmin',
  [ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_ADMIN_ADD]: 'stampUpdatedByAdmin',
  [ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_MERCHANT_REMOVE]:
    'stampUpdatedByAdminMerchant',
  [ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_MERCHANT_ADD]:
    'stampUpdatedByAdminMerchant',
  [ACTIVITY_LOG_TYPES.STAMP_TRANSFERRED]: 'stampTransferred',
};

const CUSTOMER_LOGIN_NAME_MAP = {
  [ACTIVITY_LOG_TYPES.CUSTOMER_LOGIN]: 'customerLoginLog',
};

const CUSTOMER_LOGOUT_NAME_MAP = {
  [ACTIVITY_LOG_TYPES.CUSTOMER_LOGOUT]: 'customerLogoutLog',
};

function getDisplayActivityLog(activityLog) {
  switch (activityLog.activity_type) {
    case ACTIVITY_LOG_TYPES.CUSTOMER_REWARD_EARNED:
      if (activityLog.log_info.campaign_type === CAMPAIGN_TYPE_STAMP) {
        let title = 'campaign_point_earned';
        let icon = '/activityLog/stamp-points.png';
        let srcSet =
          '/activityLog/stamp-points@2x.png 2x, /activityLog/stamp-points@3x.png 3x';
        if (activityLog.log_info?.coupon && activityLog.log_info.coupon > 0) {
          title = 'campaign_coupon_earned';
          icon = '/activityLog/stamp-coupon.png';
          srcSet =
            '/activityLog/stamp-coupon@2x.png 2x, /activityLog/stamp-coupon@3x.png 3x';
        }
        return {
          title,
          icon,
          srcSet,
        };
      }
      if (!activityLog.log_info.campaign_earning_type) {
        return {
          title: 'campaign_point_earned',
          icon: null,
          srcSet: null,
        };
      }
      const data =
        EARNING_TYPES_MAP[activityLog.log_info.campaign_earning_type];
      return {
        title: data?.title,
        icon: data?.icon,
        srcSet: data?.srcSet,
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_EXPIRED:
      return {
        title: 'campaign_point_expired',
        icon: '/activityLog/points-expired.png',
        srcSet:
          '/activityLog/points-expired@2x.png 2x, /activityLog/points-expired@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_ADMIN_ADDED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_ADMIN_REMOVED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_ADMIN_AUTO_ADDED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_ADMIN_AUTO_REMOVED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_MERCHANT_ADDED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_MERCHANT_REMOVED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_REWARD_RECALL:
    case ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_ADMIN_GRANT:
    case ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_ADMIN_GRANT:
    case ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_ADMIN_RECLAIM:
    case ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_ADMIN_RECLAIM:
    case ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_MERCHANT_GRANT:
    case ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_MERCHANT_GRANT:
    case ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_MERCHANT_RECLAIM:
    case ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_MERCHANT_RECLAIM:
    case ACTIVITY_LOG_TYPES.ADMIN_COUPON_USED:
    case ACTIVITY_LOG_TYPES.ADMIN_TICKET_USED:
    case ACTIVITY_LOG_TYPES.MERCHANT_COUPON_USED:
    case ACTIVITY_LOG_TYPES.MERCHANT_TICKET_USED:
      return {
        title: ADMIN_EDIT_NAMES_MAP[activityLog.activity_type],
        icon: '/activityLog/admin-edit.png',
        srcSet:
          '/activityLog/admin-edit@2x.png 2x, /activityLog/admin-edit@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_LEVEL_CHANGED:
      return {
        title: 'campaign_level_changed',
        icon: '/activityLog/level-change.png',
        srcSet:
          '/activityLog/level-change@2x.png 2x, /activityLog/level-change@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_LEVEL_RETAINED:
      return {
        title: 'campaign_level_retained',
        icon: '/activityLog/level-change.png',
        srcSet:
          '/activityLog/level-change@2x.png 2x, /activityLog/level-change@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_EXPIRED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_EXPIRED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_COUPON_USED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_TICKET_USED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_USED:
    case ACTIVITY_LOG_TYPES.COUPON_RETURNED:
    case ACTIVITY_LOG_TYPES.COUPON_TRANSFERRED:
    case ACTIVITY_LOG_TYPES.TICKET_TRANSFERRED:
      let couponLogTitle = COUPON_LOGS_NAME_MAP[activityLog.activity_type];
      if (
        activityLog.activity_type === ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_USED &&
        activityLog.log_info?.campaign_type === CAMPAIGN_TYPE_TICKET
      ) {
        couponLogTitle = 'ticketRedemption';
      }
      return {
        title: couponLogTitle,
        icon: '/activityLog/coupon-redemption.png',
        srcSet:
          '/activityLog/coupon-redemption@2x.png 2x, /activityLog/coupon-redemption@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_STAMP_EARNED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_STAMP_EXPIRED:
    case ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_ADMIN_RECLAIM:
    case ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_ADMIN_REMOVE:
    case ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_ADMIN_ADD:
    case ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_MERCHANT_REMOVE:
    case ACTIVITY_LOG_TYPES.CUSTOMER_STAMPS_MERCHANT_ADD:
    case ACTIVITY_LOG_TYPES.STAMP_TRANSFERRED:
      return {
        title: STAMP_LOGS_NAME_MAP[activityLog.activity_type],
        icon: '/activityLog/stamp-badge.png',
        srcSet:
          '/activityLog/stamp-badge@2x.png 2x, /activityLog/stamp-badge@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_LOGIN:
      return {
        title: CUSTOMER_LOGIN_NAME_MAP[activityLog.activity_type],
        icon: '/activityLog/login.png',
        srcSet: '/activityLog/login@2x.png 2x, /activityLog/login@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_LOGOUT:
      return {
        title: CUSTOMER_LOGOUT_NAME_MAP[activityLog.activity_type],
        icon: '/activityLog/logout.png',
        srcSet: '/activityLog/logout@2x.png 2x, /activityLog/logout@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_CASH_DOLLAR_ON_SHOPIFY:
    case ACTIVITY_LOG_TYPES.CUSTOMER_CASH_DOLLAR_BY_MERCHANT_SCANNING:
    case ACTIVITY_LOG_TYPES.MERCHANT_DIRECT_USE_CASH_DOLLAR:
      return {
        title: 'cash_dollar_log',
        icon: '/profile/currentPoint/cash-dollar-log.png',
        srcSet:
          '/profile/currentPoint/cash-dollar-log@2x.png 2x, /profile/currentPoint/cash-dollar-log@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_USE_GIFT_CARD:
      return {
        title: 'gift_card_point',
        icon: '/activityLog/gift.png',
        srcSet: '/activityLog/gift@2x.png 2x, /activityLog/gift@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.PURCHASE_REFUND:
      return {
        title: 'activityLogRefund',
        icon: '/activityLog/purchase.png',
        srcSet:
          '/activityLog/purchase@2x.png 2x, /activityLog/purchase@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.PURCHASE_PARTIAL_REFUND:
      return {
        title: 'activityLogPartialRefund',
        icon: '/activityLog/purchase.png',
        srcSet:
          '/activityLog/purchase@2x.png 2x, /activityLog/purchase@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CASH_DOLLAR_RETURNED:
      return {
        title: 'activityLogCashDollarReturned',
        icon: '/profile/currentPoint/cash-dollar-log.png',
        srcSet:
          '/profile/currentPoint/cash-dollar-log@2x.png 2x, /profile/currentPoint/cash-dollar-log@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.POINT_TRANSFERRED:
      return {
        title: 'pointTransferred',
        icon: '/activityLog/points.png',
        srcSet: '/activityLog/points@2x.png 2x, /activityLog/points@3x.png 3x',
      };
    case ACTIVITY_LOG_TYPES.CUSTOMER_TRANSFERRED_BY_ADMIN:
      return {
        title: 'customerTransferredByAdmin',
        icon: '/activityLog/customer-transferred-by-admin.png',
        srcSet:
          '/activityLog/customer-transferred-by-admin@2x.png 2x, /activityLog/customer-transferred-by-admin@3x.png 3x',
      };
    default:
      return {
        title: '',
        icon: '',
        srcSet: '',
      };
  }
}

export const getLogRewardItem = (value, suffix) => {
  if (value >= 0) {
    return `+${enLocaleNumberFormatter(value)} ${suffix}`;
  }
  return `${enLocaleNumberFormatter(value)} ${suffix}`;
};

const getReachLimitTitle = (limitTarget, limitPeriod) => {
  if (limitTarget === EarningRuleGlobalLimitTarget.PER_TRANSACTION) {
    return (
      "You've exceeded the per transaction maximum reward limit." +
      i18n.t('records:limit_reached_check')
    );
  }
  if (limitTarget === EarningRuleGlobalLimitTarget.PER_MEMBER) {
    switch (limitPeriod) {
      case EarningRuleGlobalLimitPeriod.PER_DAY:
        return (
          i18n.t('records:limit_reached_daily') +
          i18n.t('records:limit_reached_check')
        );
      case EarningRuleGlobalLimitPeriod.PER_WEEK:
        return (
          i18n.t('records:limit_reached_weekly') +
          i18n.t('records:limit_reached_check')
        );
      case EarningRuleGlobalLimitPeriod.PER_MONTH:
        return (
          i18n.t('records:limit_reached_monthly') +
          i18n.t('records:limit_reached_check')
        );
      case EarningRuleGlobalLimitPeriod.PER_YEAR:
        return (
          i18n.t('records:limit_reached_yearly') +
          i18n.t('records:limit_reached_check')
        );
      case EarningRuleGlobalLimitPeriod.ALL_TIME:
      default:
        return (
          i18n.t('records:limit_reached_total') +
          i18n.t('records:limit_reached_check')
        );
    }
  }
  if (limitTarget === EarningRuleGlobalLimitTarget.REWARD_PROGRAM) {
    return '';
  }
  return '';
};

const getOriginRewardLimitErrors = ({
  limitPeriod,
  maximumLimit,
  accumulatedReward,
  originalReward,
  finalReward,
  rewardUnit,
}) => {
  let period;
  switch (limitPeriod) {
    case EarningRuleGlobalLimitPeriod.PER_DAY:
      period = i18n.t('records:daily');
      break;
    case EarningRuleGlobalLimitPeriod.PER_WEEK:
      period = i18n.t('records:weekly');
      break;
    case EarningRuleGlobalLimitPeriod.PER_MONTH:
      period = i18n.t('records:monthly');
      break;
    case EarningRuleGlobalLimitPeriod.PER_YEAR:
      period = i18n.t('records:yearly');
      break;
    case EarningRuleGlobalLimitPeriod.ALL_TIME:
    default:
      period = i18n.t('records:all_time');
      break;
  }
  const errors = [
    {
      isPrimary: false,
      showItem: true,
      title: i18n.t('records:maximum_reward_limit', { period }),
      boldValues:
        maximumLimit?.map((item, index) =>
          rewardUnit?.[index]
            ? `${enLocaleIntegerFormatter(item)} ${rewardUnit?.[index]}`
            : enLocaleDollarFormatter(item),
        ) ?? [],
      normalValues: [],
    },
    {
      isPrimary: false,
      showItem:
        accumulatedReward?.reduce(
          (partialSum, a) => partialSum + Number(a),
          0,
        ) > 0 ?? false,
      title: i18n.t('records:accumulated_reward'),
      boldValues:
        accumulatedReward?.map((item, index) =>
          rewardUnit?.[index]
            ? `${enLocaleIntegerFormatter(item)} ${rewardUnit?.[index]}`
            : enLocaleDollarFormatter(item),
        ) ?? [],
      normalValues: [],
    },
    {
      isPrimary: false,
      showItem: true,
      title: i18n.t('records:original_reward_from_this_record'),
      boldValues:
        originalReward?.map((item, index) =>
          rewardUnit?.[index]
            ? `${enLocaleIntegerFormatter(item)} ${rewardUnit?.[index]}`
            : enLocaleDollarFormatter(item),
        ) ?? [],
      normalValues: [],
    },
    {
      isPrimary: true,
      showItem: true,
      title: i18n.t('records:final_reward'),
      boldValues:
        finalReward?.map((item, index) =>
          rewardUnit?.[index]
            ? `${enLocaleIntegerFormatter(item)} ${rewardUnit?.[index]}`
            : enLocaleDollarFormatter(item),
        ) ?? [],
      normalValues: [],
    },
  ];
  return errors;
};

const getRewardLimitTargetAndPeriod = (data) => {
  const limitType = data?.reach_limit_info?.limit_info_type;
  switch (limitType) {
    case EarningRuleGlobalLimitInfoType.TRANSACTIONS:
      return {
        limitType,
        limitTarget: data?.reach_limit_info?.transactions_reach_limit_target,
        limitPeriod: data?.reach_limit_info?.transactions_reach_limit_period,
      };
    case EarningRuleGlobalLimitInfoType.COUPONS:
      return {
        limitType,
        limitTarget: data?.reach_limit_info?.coupons_reach_limit_target,
        limitPeriod: data?.reach_limit_info?.coupons_reach_limit_period,
      };
    case EarningRuleGlobalLimitInfoType.STAMPS:
      return {
        limitType,
        limitTarget: data?.reach_limit_info?.stamps_reach_limit_target,
        limitPeriod: data?.reach_limit_info?.stamps_reach_limit_period,
      };
    case EarningRuleGlobalLimitInfoType.POINTS:
    case EarningRuleGlobalLimitInfoType.POINTS_AND_PROGRESSION_POINTS:
    case EarningRuleGlobalLimitInfoType.PROGRESSION_POINTS:
    default:
      return {
        limitType,
        limitTarget: data?.reach_limit_info?.points_reach_limit_target,
        limitPeriod: data?.reach_limit_info?.points_reach_limit_period,
      };
  }
};

const getOtherRewardLimitErrors = (limitType, limitPeriod, data) => {
  if (limitType === EarningRuleGlobalLimitInfoType.COUPONS) {
    const maximumLimit = [data?.reach_limit_info?.coupons_limit_value];
    const accumulatedReward = [data?.reach_limit_info?.coupons_total_earned];
    const originalReward = [data?.reach_limit_info?.coupons_value];
    const finalReward = [data?.reach_limit_info?.coupons_value_after_limit];
    const rewardUnit = ['coupons'];
    return getOriginRewardLimitErrors({
      limitPeriod,
      maximumLimit,
      accumulatedReward,
      originalReward,
      finalReward,
      rewardUnit,
    });
  }
  if (limitType === EarningRuleGlobalLimitInfoType.STAMPS) {
    const maximumLimit = [data?.reach_limit_info?.stamps_limit_value];
    const accumulatedReward = [data?.reach_limit_info?.stamps_total_earned];
    const originalReward = [data?.reach_limit_info?.stamps_value];
    const finalReward = [data?.reach_limit_info?.stamps_value_after_limit];
    const rewardUnit = ['stamps'];
    return getOriginRewardLimitErrors({
      limitPeriod,
      maximumLimit,
      accumulatedReward,
      originalReward,
      finalReward,
      rewardUnit,
    });
  }
  if (limitType === EarningRuleGlobalLimitInfoType.POINTS) {
    const maximumLimit = [data?.reach_limit_info?.points_limit_value];
    const accumulatedReward = [data?.reach_limit_info?.points_total_earned];
    const originalReward = [data?.reach_limit_info?.points_value];
    const finalReward = [data?.reach_limit_info?.points_value_after_limit];
    const rewardUnit = ['pts'];
    return getOriginRewardLimitErrors({
      limitPeriod,
      maximumLimit,
      accumulatedReward,
      originalReward,
      finalReward,
      rewardUnit,
    });
  }
  if (
    limitType === EarningRuleGlobalLimitInfoType.POINTS_AND_PROGRESSION_POINTS
  ) {
    const maximumLimit = [
      data?.reach_limit_info?.points_limit_value,
      data?.reach_limit_info?.progression_points_limit_value,
    ];
    const accumulatedReward = [
      data?.reach_limit_info?.points_total_earned,
      data?.reach_limit_info?.progression_points_total_earned,
    ];
    const originalReward = [
      data?.reach_limit_info?.points_value,
      data?.reach_limit_info?.progression_points_value,
    ];
    const finalReward = [
      data?.reach_limit_info?.points_value_after_limit,
      data?.reach_limit_info?.progression_points_value_after_limit,
    ];
    const rewardUnit = ['pts', 'pp'];
    return getOriginRewardLimitErrors({
      limitPeriod,
      maximumLimit,
      accumulatedReward,
      originalReward,
      finalReward,
      rewardUnit,
    });
  }
  if (limitType === EarningRuleGlobalLimitInfoType.PROGRESSION_POINTS) {
    const maximumLimit = [
      data?.reach_limit_info?.progression_points_limit_value,
    ];
    const accumulatedReward = [
      data?.reach_limit_info?.progression_points_total_earned,
    ];
    const originalReward = [data?.reach_limit_info?.progression_points_value];
    const finalReward = [
      data?.reach_limit_info?.progression_points_value_after_limit,
    ];
    const rewardUnit = ['pp'];
    return getOriginRewardLimitErrors({
      limitPeriod,
      maximumLimit,
      accumulatedReward,
      originalReward,
      finalReward,
      rewardUnit,
    });
  }
  return [];
};

const getTransactionRewardLimitErrors = (limitType, limitPeriod, data) => {
  if (limitType !== EarningRuleGlobalLimitInfoType.TRANSACTIONS) {
    return [];
  }
  const maximumLimit = [data?.reach_limit_info?.transactions_limit_value];
  const accumulatedReward = [data?.reach_limit_info?.transactions_total_earned];
  const originalReward = [data?.reach_limit_info?.transactions_value];
  const finalReward = [data?.reach_limit_info?.transactions_value_after_limit];
  const rewardUnit = [''];
  const transactionErrors = getOriginRewardLimitErrors({
    limitPeriod,
    maximumLimit,
    accumulatedReward,
    originalReward,
    finalReward,
    rewardUnit,
  });

  const transactionLimitType = data?.reach_limit_info?.transactions_reward_type;
  const otherErrors = getOtherRewardLimitErrors(
    transactionLimitType,
    limitPeriod,
    data,
  );
  const combineErrors = [
    {
      isPrimary: false,
      showItem: true,
      title: transactionErrors?.[0]?.title,
      boldValues: transactionErrors?.[0]?.boldValues ?? [],
      normalValues: otherErrors?.[0]?.boldValues ?? [],
    },
    {
      isPrimary: false,
      showItem: transactionErrors?.[1]?.showItem || otherErrors?.[1]?.showItem,
      title: i18n.t('records:accumulated_spend'),
      boldValues: transactionErrors?.[1]?.boldValues ?? [],
      normalValues: otherErrors?.[1]?.boldValues ?? [],
    },
    {
      ...transactionErrors?.[2],
      title: i18n.t('records:original_transaction_value'),
    },
    otherErrors?.[2],
    otherErrors?.[3],
  ];
  return combineErrors;
};

function assembleActivityLog(activityLog) {
  const activityType = activityLog.activity_type;
  const logInfo = activityLog.log_info || {};
  const {
    campaign_name: campaignName,
    coupon_name: couponName,
    coupon,
    content,
    points,
    tpe,
    stamps,
    new_level_name: levelName,
    campaign_type: campaignType,
    campaign_earning_type: earningType,
    cash_dollar: cashDollar,
    cash_dollar_used_in_store: cashDollarUsedInStore,
  } = logInfo;
  const { title, icon, srcSet } = getDisplayActivityLog(activityLog);

  const activityDate = getActivityLogDate(activityLog.activity_time);
  const activityTime = getActivityLogDateTime(activityLog.activity_time);
  const activityId = activityLog.id;
  const language = getCMSLanguage(i18n.language);
  let campaignTitle = campaignName?.en;
  if (campaignName && language in campaignName) {
    campaignTitle = campaignName[language] || campaignTitle;
  }
  let level = levelName?.en;
  if (levelName && language in levelName) {
    level = levelName[language] || level;
  }
  let couponTitle = couponName?.en;
  if (couponName && language in couponName) {
    couponTitle = couponName[language] || couponTitle;
  }
  let cashDollarContent = null;
  let displayTpe = tpe;
  if (cashDollar) {
    displayTpe = null;
    const storeName =
      cashDollarUsedInStore?.store_name?.[language] ||
      cashDollarUsedInStore?.store_name?.['en'] ||
      '';
    if (activityType === ACTIVITY_LOG_TYPES.CUSTOMER_CASH_DOLLAR_ON_SHOPIFY) {
      cashDollarContent = i18n.t('use_x_cash_dollar_in_online_store', {
        cashDollar: cashDollar,
      });
    } else if (
      activityType ===
      ACTIVITY_LOG_TYPES.CUSTOMER_CASH_DOLLAR_BY_MERCHANT_SCANNING
    ) {
      cashDollarContent = i18n.t('use_x_cash_dollar_in_store', {
        cashDollar: cashDollar,
        storeName: storeName,
      });
    } else if (
      activityType === ACTIVITY_LOG_TYPES.MERCHANT_DIRECT_USE_CASH_DOLLAR
    ) {
      cashDollarContent = i18n.t('use_x_cash_dollar_by_merchant', {
        cashDollar: cashDollar,
        storeName: storeName,
      });
    } else {
      i18n.t('x_cash_dollar', { cashDollar: cashDollar });
    }
  }
  let subTitle = campaignTitle || couponTitle || cashDollarContent || content;
  if (
    [
      ACTIVITY_LOG_TYPES.CUSTOMER_LEVEL_CHANGED,
      ACTIVITY_LOG_TYPES.CUSTOMER_LEVEL_RETAINED,
    ].includes(activityType)
  ) {
    subTitle = '';
  }

  // CUSTOMER_POINTS_USED contains coupon and ticket
  let finalCoupon = coupon;
  let ticket = null;
  if (
    activityType === ACTIVITY_LOG_TYPES.CUSTOMER_POINTS_USED &&
    [CAMPAIGN_TYPE_TICKET, CAMPAIGN_TYPE_COUPON].includes(campaignType)
  ) {
    displayTpe = null;
    if (campaignType === CAMPAIGN_TYPE_TICKET) {
      finalCoupon = null;
      ticket = coupon;
    }
  }

  const reachLimit = activityLog?.reach_limit ?? false;

  let transactionInfo = null;
  const {
    id,
    date,
    invoice_id: invoiceID,
    paid_amount: paidAmount,
    total_value: totalValue,
  } = activityLog.transaction_info || {};
  if (id) {
    transactionInfo = {
      id,
      date: getActivityLogDateTime(date),
      invoiceID,
      showEligible: !!paidAmount,
      paidAmount: enLocaleDollarFormatter(paidAmount),
      totalValue: enLocaleDollarFormatter(totalValue),
    };
  }

  return {
    activityType,
    campaignType,
    reachLimit,
    earningType,
    activityDate,
    title,
    subTitle,
    icon,
    srcSet,
    activityTime,
    points,
    tpe: displayTpe,
    coupon: finalCoupon,
    ticket,
    stamps,
    level,
    activityId,
    transactionInfo,
  };
}

function groupActivityLogs(logs, key) {
  const logInfo = logs[key];
  return {
    title: key,
    data: logInfo,
  };
}

const getInitState = () => ({
  showNonReward: false,
  activityLogList: [],
  activityLogOriginList: [],
  activityLogPageLink: null,
  isLoading: false,
  activityLogDetail: {},
});

export default {
  namespace: 'activityLogs',
  state: getInitState(),
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    setUserLogs(state, { payload }) {
      const newLogs = payload.logs;
      if (!newLogs) {
        return {
          ...state,
        };
      }
      const formattedLog = newLogs.map((log) => assembleActivityLog(log));
      const originalLogs = state.activityLogOriginList;
      const totalLog = payload.init
        ? formattedLog
        : originalLogs.concat(formattedLog);
      const groupedLogs = groupBy(totalLog, 'activityTime');
      const activityLogList = Object.keys(groupedLogs).map((key) =>
        groupActivityLogs(groupedLogs, key),
      );
      return {
        ...state,
        activityLogList,
        activityLogOriginList: totalLog,
        activityLogPageLink: payload.activityLogPageLink,
      };
    },
    clearLogs() {
      return getInitState();
    },
    assembleActivityLogDetail(state, { payload }) {
      const { data } = payload;
      const activityLogDetail = assembleActivityLog(data);
      if (!activityLogDetail?.reachLimit) {
        return {
          ...state,
          activityLogDetail,
        };
      }
      const { limitType, limitTarget, limitPeriod } =
        getRewardLimitTargetAndPeriod(data);
      const title = getReachLimitTitle(limitTarget, limitPeriod);
      let errors = [];
      if (limitType === EarningRuleGlobalLimitInfoType.PROGRESSION_POINTS) {
        errors = []; // TODO: not implemented
      } else if (limitType === EarningRuleGlobalLimitInfoType.TRANSACTIONS) {
        errors = getTransactionRewardLimitErrors(limitType, limitPeriod, data);
      } else {
        errors = getOtherRewardLimitErrors(limitType, limitPeriod, data);
      }
      activityLogDetail.rewardLimitDetail = {
        title,
        errors,
      };
      return {
        ...state,
        activityLogDetail,
      };
    },
  },
  effects: {
    fetchUserActivityLogs: [
      function* (action, { call, put, select }) {
        const { showNonReward } = action?.payload ?? {};
        const uid = yield select((state) => state.users.userInfo.uid);
        const accessToken = yield select((state) => state.users.accessToken);
        yield put(
          createAction('updateState')({
            isLoading: true,
            showNonReward: !!showNonReward,
          }),
        );
        const response = yield call(ssoAPIHelper.getUserActivityLogs, {
          accessToken,
          uid,
          levelRelated: !ServerConfig.getNoLevel(),
          showNonReward,
        });
        if (response.status === 200) {
          const { data } = response;
          const activityLogPageLink = subHeaderLink(response.headers.link);
          yield put(
            createAction('setUserLogs')({
              logs: data,
              activityLogPageLink,
              init: true,
            }),
          );
          yield put(
            createAction('updateState')({
              isLoading: false,
            }),
          );
        }
      },
      { type: 'takeLatest' },
    ],

    uerActivityLogsLoadMore: [
      function* (action, { call, put, select }) {
        const accessToken = yield select((state) => state.users.accessToken);
        const pageLink = yield select(
          (state) => state.activityLogs.activityLogPageLink,
        );
        if (!pageLink) {
          return;
        }
        const response = yield call(ssoAPIHelper.getUserActivityLogs, {
          accessToken,
          pageLink,
        });
        if (response.status === 200) {
          const { data } = response;
          const activityLogPageLink = subHeaderLink(response.headers.link);
          yield put(
            createAction('setUserLogs')({
              logs: data,
              activityLogPageLink,
              levelRelated: true,
            }),
          );
        }
        yield put(
          createAction('pullrefreshandloadmore/updateState')({
            ActivityLogLoadingMore: false,
          }),
        );
      },
      { type: 'takeLatest' },
    ],
    userActivityLogDetail: [
      function* ({ payload }, { call, put, select }) {
        const { id, failAction } = payload;
        yield put(
          createAction('updateState')({
            activityLogDetail: {},
          }),
        );
        const uid = yield select((state) => state.users.userInfo.uid);
        const accessToken = yield select((state) => state.users.accessToken);
        const response = yield call(ssoAPIHelper.getActivityLogDetail, {
          accessToken,
          uid,
          id,
        });
        if (response.status < 300) {
          const { data } = response;
          yield put(createAction('assembleActivityLogDetail')({ data }));
        } else if (failAction) {
          yield failAction();
        }
      },
      { type: 'takeLatest' },
    ],
  },
};
